import { useEffect, useMemo, useState } from 'react'
import { AppHeader } from './components/AppHeader'
import { ProgressBar } from './components/ProgressBar'
import { QuestionCard } from './components/QuestionCard'
import { loadQuestions, loadSources, loadStudySupports } from './data/csv'
import { LoginScreen } from './pages/LoginScreen'
import { WelcomeScreen } from './pages/WelcomeScreen'
import { ResultsScreen } from './pages/ResultsScreen'
import { ReviewScreen } from './pages/ReviewScreen'
import { TeacherScreen } from './pages/TeacherScreen'
import { retryPendingResults } from './services/pendingResults'
import { clearSessionProgress, readSessionProgress, saveSessionProgress, type StoredSessionProgress } from './services/sessionProgress'
import type { AccessibilitySettings, AccessPayload, Question, QuestionResult, SessionConfig, SessionResult, Source, StudySupport, SupportSettings } from './types'
import { selectAssignedQuestions } from './utils/questionSelection'
import { calculateResults, createRecommendation, findWeakestTopic } from './utils/scoring'
import { createSessionId } from './utils/session'

const DEFAULT_SETTINGS: AccessibilitySettings = { concentrationMode: false, soundEnabled: false, celebrationsEnabled: true, textScale: 1 }
type Screen = 'login' | 'welcome' | 'session' | 'results' | 'review' | 'teacher'

function readSettings(): AccessibilitySettings {
  try { return { ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem('history-accessibility') ?? '{}') } }
  catch { return DEFAULT_SETTINGS }
}

function mergeSessionSupport(access: AccessPayload): SupportSettings {
  const base = access.student.support
  const vocabularySimplified = access.session.vocabularyLevel === 'Simplificado'
  const moderate = access.session.supportIntensity === 'Moderado'
  const intensive = access.session.supportIntensity === 'Intensivo'
  return {
    ...base,
    shortPrompts: base.shortPrompts || intensive,
    specificHints: base.specificHints || moderate || intensive,
    reducedDistractions: base.reducedDistractions || intensive,
    shorterSources: base.shorterSources || moderate || intensive,
    simplifiedVocabulary: base.simplifiedVocabulary || vocabularySimplified,
    enlargedImages: base.enlargedImages || moderate || intensive,
  }
}

function supportScore(item: StudySupport, access: AccessPayload, weakestTopic: string): number {
  let score = 0
  if (item.unit === access.session.unit) score += 5
  if (item.topic === weakestTopic) score += 8
  if (item.oa && access.session.selectedOAs.includes(item.oa)) score += 2
  if (item.difficulty === access.session.difficulty) score += 3
  if (item.vocabularyLevel === access.session.vocabularyLevel) score += 3
  if (item.supportIntensity === access.session.supportIntensity) score += 2
  if (item.difficulty === 'General') score += 1
  if (item.vocabularyLevel === 'Todos') score += 1
  if (item.supportIntensity === 'Todos') score += 1
  return score
}

function playSuccessSound() {
  const context = new window.AudioContext(); const oscillator = context.createOscillator(); const gain = context.createGain()
  oscillator.frequency.value = 660; gain.gain.setValueAtTime(0.08, context.currentTime); gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.18)
  oscillator.connect(gain); gain.connect(context.destination); oscillator.start(); oscillator.stop(context.currentTime + 0.18)
}

export default function App() {
  const [screen, setScreen] = useState<Screen>('login')
  const [settings, setSettings] = useState<AccessibilitySettings>(readSettings)
  const [questions, setQuestions] = useState<Question[]>([])
  const [sources, setSources] = useState<Source[]>([])
  const [supports, setSupports] = useState<StudySupport[]>([])
  const [loadError, setLoadError] = useState('')
  const [access, setAccess] = useState<AccessPayload | null>(null)
  const [resumeProgress, setResumeProgress] = useState<StoredSessionProgress | null>(null)
  const [sessionQuestions, setSessionQuestions] = useState<Question[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [questionResults, setQuestionResults] = useState<QuestionResult[]>([])
  const [streak, setStreak] = useState(0)
  const [sessionStart, setSessionStart] = useState(0)
  const [sessionResult, setSessionResult] = useState<SessionResult | null>(null)
  const [celebrate, setCelebrate] = useState(false)

  useEffect(() => {
    Promise.all([loadQuestions(), loadSources(), loadStudySupports()])
      .then(([loadedQuestions, loadedSources, loadedSupports]) => { setQuestions(loadedQuestions); setSources(loadedSources); setSupports(loadedSupports) })
      .catch((reason) => setLoadError(reason instanceof Error ? reason.message : 'No se pudo cargar el contenido.'))
    retryPendingResults().catch(() => undefined)
  }, [])

  useEffect(() => {
    localStorage.setItem('history-accessibility', JSON.stringify(settings))
    document.documentElement.style.setProperty('--text-scale', String(settings.textScale))
    document.body.classList.toggle('concentration-mode', settings.concentrationMode)
  }, [settings])

  const availableQuestions = useMemo(() => {
    if (!access) return []
    return selectAssignedQuestions(questions, access.session, access.student, () => 0.5)
  }, [access, questions])

  const persistProgress = () => {
    if (!access || screen !== 'session' || sessionQuestions.length === 0) return
    saveSessionProgress(access, {
      questionIds: sessionQuestions.map((question) => question.id),
      currentIndex,
      questionResults,
      streak,
      elapsedSeconds: Math.max(0, Math.round((Date.now() - sessionStart) / 1000)),
    })
  }

  useEffect(() => {
    if (screen !== 'session') return
    const beforeUnload = (event: BeforeUnloadEvent) => {
      persistProgress()
      event.preventDefault()
      event.returnValue = ''
    }
    const pageHide = () => persistProgress()
    window.addEventListener('beforeunload', beforeUnload)
    window.addEventListener('pagehide', pageHide)
    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
      window.removeEventListener('pagehide', pageHide)
    }
  })

  const acceptAccess = (nextAccess: AccessPayload) => {
    setAccess(nextAccess)
    setResumeProgress(readSessionProgress(nextAccess, questions))
    setScreen('welcome')
  }

  const startSession = () => {
    if (!access) return
    if (resumeProgress) {
      const byId = new Map(questions.map((question) => [question.id, question]))
      const restored = resumeProgress.questionIds.map((id) => byId.get(id)).filter((item): item is Question => Boolean(item))
      if (restored.length === resumeProgress.questionIds.length) {
        setSessionQuestions(restored)
        setCurrentIndex(resumeProgress.currentIndex)
        setQuestionResults(resumeProgress.questionResults)
        setStreak(resumeProgress.streak)
        setSessionStart(Date.now() - resumeProgress.elapsedSeconds * 1000)
        setSessionResult(null)
        setScreen('session')
        return
      }
    }
    const selected = selectAssignedQuestions(questions, access.session, access.student)
    const start = Date.now()
    setSessionQuestions(selected); setCurrentIndex(0); setQuestionResults([]); setStreak(0); setSessionStart(start); setSessionResult(null); setScreen('session')
    const stored = saveSessionProgress(access, { questionIds: selected.map((question) => question.id), currentIndex: 0, questionResults: [], streak: 0, elapsedSeconds: 0 })
    setResumeProgress(stored)
  }

  const finishSession = (allResults: QuestionResult[]) => {
    if (!access) return
    clearSessionProgress(access)
    setResumeProgress(null)
    const calculations = calculateResults(allResults)
    const weakestTopic = findWeakestTopic(allResults)
    const config: SessionConfig = {
      sessionId: access.session.id, sessionTitle: access.session.title, selectedOAs: access.session.selectedOAs,
      topics: access.session.topics, difficulty: access.session.difficulty, vocabularyLevel: access.session.vocabularyLevel, supportIntensity: access.session.supportIntensity, questionCount: sessionQuestions.length,
      orderMode: access.session.orderMode, completionAction: access.session.completionAction,
      supportMaterialId: access.session.supportMaterialId, notebookActivity: access.session.notebookActivity, unit: access.session.unit,
    }
    const matchingSupport = supports.find((item) => item.id === access.session.supportMaterialId)
      ?? [...supports].sort((a, b) => supportScore(b, access, weakestTopic) - supportScore(a, access, weakestTopic))[0]
    const result: SessionResult = {
      sessionId: createSessionId(), assignmentId: access.session.id, timestamp: new Date().toISOString(), student: access.student, config,
      score: calculations.correctCount * 100, percentage: calculations.percentage, correctCount: calculations.correctCount,
      incorrectCount: calculations.incorrectCount, attempts: allResults.reduce((sum, item) => sum + item.attempts, 0),
      durationSeconds: Math.max(1, Math.round((Date.now() - sessionStart) / 1000)), resultsByOA: calculations.resultsByOA,
      incorrectIds: allResults.filter((item) => item.correct === false).map((item) => item.questionId), questionResults: allResults,
      recommendation: createRecommendation(calculations.resultsByOA, weakestTopic), weakestTopic,
      supportMaterialId: matchingSupport?.id ?? access.session.supportMaterialId,
    }
    setSessionResult(result); setScreen('results')
  }

  const completeQuestion = (result: QuestionResult) => {
    if (!access) return
    const nextResults = [...questionResults, result]
    const nextStreak = result.correct === true ? streak + 1 : result.correct === false ? 0 : streak
    setQuestionResults(nextResults)
    setStreak(nextStreak)
    if (result.correct === true) {
      if (settings.soundEnabled) playSuccessSound()
      if (settings.celebrationsEnabled) { setCelebrate(true); window.setTimeout(() => setCelebrate(false), 850) }
    }
    if (currentIndex + 1 >= sessionQuestions.length) {
      finishSession(nextResults)
    } else {
      const nextIndex = currentIndex + 1
      setCurrentIndex(nextIndex)
      const stored = saveSessionProgress(access, {
        questionIds: sessionQuestions.map((question) => question.id),
        currentIndex: nextIndex,
        questionResults: nextResults,
        streak: nextStreak,
        elapsedSeconds: Math.max(0, Math.round((Date.now() - sessionStart) / 1000)),
      })
      setResumeProgress(stored)
    }
  }

  const pauseSession = () => {
    if (!access) return
    persistProgress()
    const confirmed = window.confirm('Tu avance está guardado. ¿Quieres pausar la sesión y volver a la pantalla de acceso?')
    if (!confirmed) return
    setScreen('login'); setAccess(null); setSessionQuestions([]); setSessionResult(null); setResumeProgress(null)
  }

  const continueToNextSession = () => {
    if (!access?.nextSession) return
    const nextAccess = { ...access, session: access.nextSession, nextSession: undefined, pendingSessions: Math.max(1, access.pendingSessions - 1) }
    setAccess(nextAccess)
    setResumeProgress(readSessionProgress(nextAccess, questions))
    setSessionQuestions([]); setSessionResult(null); setScreen('welcome')
  }

  const goHome = () => { setScreen('login'); setAccess(null); setSessionQuestions([]); setSessionResult(null); setResumeProgress(null) }
  const currentQuestion = sessionQuestions[currentIndex]
  const effectiveSupport = access ? mergeSessionSupport(access) : null
  const source = currentQuestion ? sources.find((item) => item.id === currentQuestion.sourceId) : undefined
  const reviewQuestions = sessionResult ? sessionResult.incorrectIds.map((id) => questions.find((question) => question.id === id)).filter((item): item is Question => Boolean(item)) : []
  const support = sessionResult ? supports.find((item) => item.id === sessionResult.supportMaterialId) : undefined

  return (
    <div className={settings.concentrationMode ? 'app concentration' : 'app'}>
      <AppHeader settings={settings} onSettingsChange={setSettings} onHome={screen === 'login' || screen === 'session' ? undefined : goHome} />
      {celebrate && <div className="celebration" aria-hidden="true">¡Buen trabajo!</div>}
      {loadError && <main id="main-content" className="center-page"><h1>No se pudo iniciar la aplicación</h1><p className="error-message">{loadError}</p></main>}
      {!loadError && questions.length === 0 && <main id="main-content" className="center-page"><p>Cargando el banco de actividades…</p></main>}
      {!loadError && questions.length > 0 && screen === 'login' && <LoginScreen onSuccess={acceptAccess} onTeacher={() => setScreen('teacher')} />}
      {screen === 'welcome' && access && <WelcomeScreen access={access} availableQuestions={availableQuestions.length} savedProgress={resumeProgress} onStart={startSession} onExit={goHome} />}
      {screen === 'session' && currentQuestion && access && <main id="main-content" className="session-page"><ProgressBar current={currentIndex} total={sessionQuestions.length} streak={streak} studentName={access.student.firstName} onPause={pauseSession} /><QuestionCard key={`${currentQuestion.id}-${currentIndex}`} question={currentQuestion} source={source} support={effectiveSupport ?? access.student.support} onComplete={completeQuestion} /></main>}
      {screen === 'results' && sessionResult && <ResultsScreen result={sessionResult} questions={questions} support={support} onReview={() => setScreen('review')} onFinish={goHome} onNextSession={access?.nextSession ? continueToNextSession : undefined} />}
      {screen === 'review' && sessionResult && <ReviewScreen questions={reviewQuestions} sources={sources} support={effectiveSupport ?? sessionResult.student.support} onFinish={() => setScreen('results')} />}
      {screen === 'teacher' && <TeacherScreen onExit={goHome} />}
    </div>
  )
}
